import { createItemType } from 'todistukset-common/src/vtj/form-data.util'
import {
  StoreUiTodistusPyyntö,
  TodistusPyyntö,
} from 'todistukset-common/src/vtj/types/todistus'
import {
  CertificateProductType,
  TodistuksetPriceList,
  TodistusProduct,
} from 'todistukset-common/src/vtj/types/price-list.type'
import { Serialized } from 'common/src/vtj/types/util.type'
import { Payment } from 'todistukset-common/src/vtj/types/todistus-order.type'
import { getSubjectPersons } from 'todistukset-common/src/vtj/todistus-person.util'
import { finalEditableFieldValue } from 'todistukset-common/src/vtj/types/editable-field.type'

export const getTodistusProductForForm = ({
  form,
  priceList,
}: {
  form: Pick<
    TodistusPyyntö,
    'todistusType' | 'todistusSubtype' | 'includeTranslationForm'
  > & { todistusLanguage: TodistusPyyntö['todistusLanguage'] | null }
  priceList: Serialized<TodistuksetPriceList>
}): Serialized<TodistusProduct> => {
  const productType = createItemType(form)
  const product = priceList.products.find(
    (product) => product.productType === productType
  )
  if (!product) {
    throw new Error(`Found no product for product type "${productType}"`)
  }
  return product
}

export const getApostilleProduct = ({
  priceList,
}: {
  priceList: Serialized<TodistuksetPriceList>
}): Serialized<TodistusProduct> => {
  const product = priceList.products.find(
    (product) =>
      product.productType === CertificateProductType.APOSTILLE_TODISTUS
  )

  if (!product) {
    throw new Error(
      `Found no apostille product from price list "${JSON.stringify({
        priceListId: priceList.priceListId,
        description: priceList.description,
      })}"`
    )
  }
  return product
}

export const getEuVakiolomakeProduct = ({
  priceList,
}: {
  priceList: Serialized<TodistuksetPriceList>
}): Serialized<TodistusProduct> => {
  const product = priceList.products.find(
    (product) =>
      product.productType === CertificateProductType.EU_MONIKIELINEN_VAKIOLOMAKE
  )

  if (!product) {
    throw new Error(
      `Found no eu-vakiolomake product from price list "${JSON.stringify({
        priceListId: priceList.priceListId,
        description: priceList.description,
      })}"`
    )
  }
  return product
}

export const getTotalPriceForFormInEuroCents = ({
  form,
  priceList,
}: {
  form: Pick<
    StoreUiTodistusPyyntö,
    | 'todistusType'
    | 'todistusSubtype'
    | 'includeTranslationForm'
    | 'persons'
    | 'todistusLanguage'
  >
  priceList: Serialized<TodistuksetPriceList>
}): number => {
  const subjects = getSubjectPersons(form.persons)
  const amount = subjects.reduce<number>(
    (total, subject) =>
      total + (finalEditableFieldValue(subject.orderedAmount) ?? 1),
    0
  )
  const product = getTodistusProductForForm({ form, priceList })
  const productPrice = product.priceWithVatInEuroCents
  return productPrice * amount
}

export const isPaymentFreeOfCharge = (
  payment: Pick<Payment, 'costInEuroCentsWithVat'>
): boolean => payment.costInEuroCentsWithVat === 0
